import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useDocumentTitle } from "$utils/document-title";
import canaryApi from "./api";
import api from "$services/api";

const CanaryConfigPage = () => {
    useDocumentTitle("Canary Config");

    const [clients, setClients] = useState([]);
    const [randomCount, setRandomCount] = useState(0);
    const [savedCount, setSavedCount] = useState(0);
    const [randomListCount, setRandomListCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [saving, setSaving] = useState(false);
    const searchRef = useRef(null);
    const searchAbortRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        canaryApi.getConfig(controller.signal).then((config) => {
            if (!config) return;
            setClients(config.canaryClients || []);
            setRandomCount(config.canaryRandomCount || 0);
            setSavedCount(config.canaryRandomCount || 0);
            setRandomListCount(config.canaryRandomListCount || 0);
        });
        return () => controller.abort();
    }, []);

    const handleSearch = async (query) => {
        setSearchQuery(query);
        if (!query || query.length < 2) {
            setSuggestions([]);
            return;
        }
        if (searchAbortRef.current) searchAbortRef.current.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;
        try {
            const response = await api.get(
                `/api/client-groups?Name=${encodeURIComponent(query)}&page=0&limit=10`,
                null,
                { signal: controller.signal }
            );
            const results = response?.data || [];
            const existing = new Set(clients.map((c) => c.clientGroupId));
            setSuggestions(results.filter((r) => !existing.has(r.id)));
        } catch (e) {
            if (!api.requestWasCanceled(e)) {
                toast("Search failed. Please try again.");
            }
        }
    };

    const handleAddClient = async (suggestion) => {
        const previous = clients;
        const updated = [...clients, { clientGroupId: suggestion.id, name: suggestion.name }];
        setClients(updated);
        setSuggestions([]);
        setSearchQuery("");
        try {
            await canaryApi.updateClients(updated);
        } catch (e) {
            if (!api.requestWasCanceled(e)) {
                toast("Failed to add client. Please try again.");
                setClients(previous);
            }
        }
    };

    const handleRemoveClient = async (clientGroupId) => {
        if (!window.confirm("Remove this client from canary tracking?")) return;
        const updated = clients.filter((c) => c.clientGroupId !== clientGroupId);
        const previous = clients;
        setClients(updated);
        try {
            await canaryApi.updateClients(updated);
        } catch (e) {
            if (!api.requestWasCanceled(e)) {
                toast("Failed to remove client. Please try again.");
                setClients(previous);
            }
        }
    };

    const handleSaveRandomCount = async () => {
        setSaving(true);
        const refreshController = new AbortController();
        try {
            await canaryApi.updateRandomCount(randomCount);
            setSavedCount(randomCount);
            const config = await canaryApi.getConfig(refreshController.signal);
            if (config) setRandomListCount(config.canaryRandomListCount || 0);
        } catch (e) {
            if (!api.requestWasCanceled(e)) {
                toast("Failed to save random count. Please try again.");
            }
        } finally {
            refreshController.abort();
            setSaving(false);
        }
    };

    const isDirty = randomCount !== savedCount;

    return (
        <div>
            <h1>Canary Records — Configuration</h1>

            <section>
                <h2>Tracked Clients</h2>
                <p>These clients are captured on every ETL run and tracked over time.</p>

                <div style={{ position: "relative" }}>
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Search for a client group..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="input"
                    />
                    {suggestions.length > 0 && (
                        <ul style={{ position: "absolute", background: "white", border: "1px solid #ccc", listStyle: "none", margin: 0, padding: "4px 0", zIndex: 100, width: "100%" }}>
                            {suggestions.map((s) => (
                                <li
                                    key={s.id}
                                    style={{ padding: "6px 12px", cursor: "pointer" }}
                                    onClick={() => handleAddClient(s)}
                                >
                                    {s.name}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {clients.length === 0 ? (
                    <p className="txt -muted">No clients tracked yet. Search above to add some.</p>
                ) : (
                    <ul style={{ listStyle: "none", padding: 0, marginTop: "12px" }}>
                        {clients.map((c) => (
                            <li key={c.clientGroupId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #eee" }}>
                                <span>{c.name}</span>
                                <button
                                    className="button -small -secondary"
                                    onClick={() => handleRemoveClient(c.clientGroupId)}
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section style={{ marginTop: "32px" }}>
                <h2>Random Spot Checks</h2>
                <p>How many random client groups should be included in the next ETL run?</p>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                        type="number"
                        min="0"
                        value={randomCount}
                        onChange={(e) => setRandomCount(parseInt(e.target.value, 10) || 0)}
                        className="input"
                        style={{ width: "80px" }}
                    />
                    <button
                        className="button -primary -small"
                        onClick={handleSaveRandomCount}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save & Generate List"}
                    </button>
                    {!saving && !isDirty && savedCount > 0 && (
                        <span className="txt -muted" style={{ fontSize: "0.875rem" }}>
                            ✓ Saved
                        </span>
                    )}
                    {!saving && isDirty && (
                        <span style={{ fontSize: "0.875rem", color: "var(--orgaimi-color-warning, #d97706)" }}>
                            Unsaved changes
                        </span>
                    )}
                </div>

                <p className="txt -muted" style={{ marginTop: "8px", fontSize: "0.875rem" }}>
                    {savedCount > 0
                        ? randomListCount > 0
                            ? `${randomListCount} random client${randomListCount !== 1 ? "s" : ""} queued for the next ETL run (configured: ${savedCount})`
                            : `Configured for ${savedCount} random client${savedCount !== 1 ? "s" : ""} per ETL run — click Save & Generate List to queue them`
                        : "Set a count and save to include random clients in the next ETL run."}
                </p>
            </section>
        </div>
    );
};

export default CanaryConfigPage;
